import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  Search,
  Loader2,
  Check,
  X,
  MessageSquare,
  Tag,
  GripVertical,
} from 'lucide-react';
import { subscribeToFAQs, createFAQ, updateFAQ, deleteFAQ } from '../../services/firestore';
import { toastSuccess, toastError, toastPromise } from './ToastProvider';
import { useConfirm, ConfirmDialog } from './ConfirmDialog';
import { StatsCardSkeleton } from './Skeleton';
import { FAQEmptyState, SearchEmptyState, ErrorState } from './EmptyState';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable FAQ Item Component
const SortableFAQItem = ({ faq, catInfo, isExpanded, onToggleExpand, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`bg-white/5 border border-[#D4AF7A]/20 rounded-xl overflow-hidden transition-all ${!faq.isActive ? 'opacity-50' : ''}`}>
      {/* Header */}
      <button
        onClick={() => onToggleExpand(faq.id)}
        className="w-full px-6 py-4 flex items-start justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-start gap-3 flex-1">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="flex-shrink-0 p-1 text-[#D4AF7A]/50 hover:text-[#D4AF7A] cursor-grab active:cursor-grabbing hover:bg-white/5 rounded transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical size={20} />
          </button>

          <div className="flex-1 text-left">
            <div className="flex items-center gap-3 mb-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border ${catInfo.color}`}
              >
                {catInfo.label}
              </span>
              {!faq.isActive && (
                <span className="px-3 py-1 bg-gray-500/20 border border-gray-500/30 rounded-full text-xs text-gray-400">
                  Tersembunyi
                </span>
              )}
            </div>
            <h4 className="text-lg font-semibold text-[#F7F5F2]">
              {faq.question}
            </h4>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <span className="text-xs text-[#F7F5F2]/60 mr-2">
            Order: {faq.order}
          </span>
          {isExpanded ? (
            <ChevronUp size={20} className="text-[#D4AF7A]" />
          ) : (
            <ChevronDown size={20} className="text-[#D4AF7A]" />
          )}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-[#D4AF7A]/20">
          <div className="py-4">
            <label className="block text-sm text-[#F7F5F2]/70 mb-2">
              Jawaban:
            </label>
            <p className="text-[#F7F5F2] leading-relaxed">{faq.answer}</p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(faq)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20"
            >
              <Edit size={16} />
              Edit
            </button>
            <button
              onClick={() => onDelete(faq.id)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/20"
            >
              <Trash2 size={16} />
              Hapus
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Confirmation dialog hook
  const { confirm, ConfirmDialog } = useConfirm();

  const [form, setForm] = useState({
    question: '',
    answer: '',
    category: 'general',
    order: 0,
    isActive: true,
  });

  useEffect(() => {
    const unsubscribe = subscribeToFAQs(
      (data) => {
        // Sort FAQs by order field
        const sortedData = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
        setFaqs(sortedData);
        setLoading(false);
        setError(null);
      }
    );
    return () => unsubscribe();
  }, []);

  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFaqs((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update order in Firestore
        newItems.forEach(async (item, index) => {
          if (item.order !== index) {
            await updateFAQ(item.id, { ...item, order: index });
          }
        });

        return newItems;
      });
    }
  };

  const categories = [
    { value: 'general', label: 'Umum', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'booking', label: 'Booking', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'payment', label: 'Pembayaran', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'service', label: 'Layanan', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  ];

  const resetForm = () => {
    setForm({
      question: '',
      answer: '',
      category: 'general',
      order: faqs.length,
      isActive: true,
    });
    setEditing(null);
  };

  const handleEdit = (faq) => {
    setEditing(faq);
    setForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order,
      isActive: faq.isActive,
    });
    setShowForm(true);
  };

  const handleDelete = async (faqId) => {
    const confirmed = await confirm('Hapus FAQ ini? Tindakan tidak dapat dibatalkan.', {
      title: 'Hapus FAQ',
      type: 'danger',
      confirmText: 'Ya, Hapus',
      cancelText: 'Batal',
    });

    if (!confirmed) return;

    try {
      await toastPromise(
        deleteFAQ(faqId),
        {
          loading: 'Menghapus FAQ...',
          success: 'FAQ berhasil dihapus!',
          error: 'Gagal menghapus FAQ',
        }
      );
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toastError('Gagal menghapus FAQ');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.question.trim() || !form.answer.trim()) {
      toastError('Pertanyaan dan jawaban wajib diisi!');
      return;
    }

    setSaving(true);
    try {
      await toastPromise(
        editing ? updateFAQ(editing.id, form) : createFAQ(form),
        {
          loading: editing ? 'Memperbarui FAQ...' : 'Menambahkan FAQ...',
          success: editing ? 'FAQ berhasil diperbarui!' : 'FAQ berhasil ditambahkan!',
          error: 'Gagal menyimpan FAQ',
        }
      );
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toastError('Gagal menyimpan FAQ');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const filteredFAQs = faqs.filter((faq) => {
    const query = searchQuery.toLowerCase();
    return (
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query) ||
      faq.category.toLowerCase().includes(query)
    );
  });

  const getCategoryInfo = (category) => {
    return categories.find((c) => c.value === category) || categories[0];
  };

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-2">
              <div className="h-8 w-48 bg-white/5 rounded animate-pulse" />
              <div className="h-4 w-64 bg-white/5 rounded animate-pulse" />
            </div>
            <div className="h-10 w-32 bg-white/5 rounded-lg animate-pulse" />
          </div>

          {/* Search Skeleton */}
          <div className="h-12 bg-white/5 border border-[#D4AF7A]/20 rounded-lg animate-pulse" />

          {/* Stats Skeleton */}
          <StatsCardSkeleton count={2} />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <ErrorState
          title="Gagal Memuat Data"
          message={error}
          onRetry={() => window.location.reload()}
        />
      )}

      {/* Content */}
      {!loading && !error && (
        <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#F7F5F2]">Management FAQ</h2>
          <p className="text-[#F7F5F2]/70 text-sm mt-1">
            Kelola Pertanyaan yang Sering Diajukan
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all"
        >
          <Plus size={18} />
          Tambah FAQ
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#F7F5F2]/50" />
        <input
          type="text"
          placeholder="Cari pertanyaan atau jawaban..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#D4AF7A]/20 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#F7F5F2]">
                  {editing ? 'Edit FAQ' : 'Tambah FAQ Baru'}
                </h3>
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="p-2 text-[#F7F5F2]/70 hover:text-[#F7F5F2] hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Question */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Pertanyaan *
                </label>
                <input
                  type="text"
                  value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })}
                  placeholder="Contoh: Berapa lama proses pengerjaan henna?"
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  required
                />
              </div>

              {/* Answer */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Jawaban *
                </label>
                <textarea
                  value={form.answer}
                  onChange={(e) => setForm({ ...form, answer: e.target.value })}
                  placeholder="Jawaban dari pertanyaan..."
                  rows={5}
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Kategori
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Urutan Tampil
                </label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) })}
                  min={0}
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                />
                <p className="text-xs text-[#F7F5F2]/60 mt-1">
                  Semakin kecil angka, semakin atas tampilannya
                </p>
              </div>

              {/* IsActive */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#D4AF7A]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF7A]" />
                </label>
                <span className="text-sm text-[#F7F5F2]/80">
                  Tampilkan di halaman public
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-[#D4AF7A]/20">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      {editing ? 'Update' : 'Simpan'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 text-[#F7F5F2] rounded-lg hover:bg-white/10"
                >
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Drag & Drop Instructions */}
      {!searchQuery && faqs.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg">
          <GripVertical size={20} className="text-[#D4AF7A]" />
          <p className="text-sm text-[#F7F5F2]/80">
            <span className="font-semibold text-[#D4AF7A]">Tips:</span> Tarik ikon ⋮⋮ untuk mengatur urutan FAQ. Urutan akan otomatis disimpan.
          </p>
        </div>
      )}

      {/* FAQ List */}
      {!searchQuery && faqs.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={faqs.map((faq) => faq.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {faqs.map((faq) => {
                const catInfo = getCategoryInfo(faq.category);
                const isExpanded = expandedItems.has(faq.id);

                return (
                  <SortableFAQItem
                    key={faq.id}
                    faq={faq}
                    catInfo={catInfo}
                    isExpanded={isExpanded}
                    onToggleExpand={toggleExpand}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-3">
          {filteredFAQs.length === 0 ? (
            searchQuery ? (
              <SearchEmptyState
                onClear={() => {
                  setSearchQuery('');
                }}
              />
            ) : (
              <FAQEmptyState
                onAdd={() => {
                  resetForm();
                  setShowForm(true);
                }}
              />
            )
          ) : (
            filteredFAQs.map((faq) => {
              const catInfo = getCategoryInfo(faq.category);
              const isExpanded = expandedItems.has(faq.id);

              return (
                <div
                  key={faq.id}
                  className={`bg-white/5 border border-[#D4AF7A]/20 rounded-xl overflow-hidden transition-all ${
                    !faq.isActive ? 'opacity-50' : ''
                  }`}
                >
                  {/* Header */}
                  <button
                    onClick={() => toggleExpand(faq.id)}
                    className="w-full px-6 py-4 flex items-start justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${catInfo.color}`}
                        >
                          {catInfo.label}
                        </span>
                        {!faq.isActive && (
                          <span className="px-3 py-1 bg-gray-500/20 border border-gray-500/30 rounded-full text-xs text-gray-400">
                            Tersembunyi
                          </span>
                        )}
                      </div>
                      <h4 className="text-lg font-semibold text-[#F7F5F2]">
                        {faq.question}
                      </h4>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-xs text-[#F7F5F2]/60 mr-2">
                        Order: {faq.order}
                      </span>
                      {isExpanded ? (
                        <ChevronUp size={20} className="text-[#D4AF7A]" />
                      ) : (
                        <ChevronDown size={20} className="text-[#D4AF7A]" />
                      )}
                    </div>
                  </button>

                  {/* Content */}
                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-[#D4AF7A]/20">
                      <div className="py-4">
                        <label className="block text-sm text-[#F7F5F2]/70 mb-2">
                          Jawaban:
                        </label>
                        <p className="text-[#F7F5F2] leading-relaxed">{faq.answer}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(faq)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20"
                        >
                          <Edit size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(faq.id)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 size={16} />
                          Hapus
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Results Count */}
      {filteredFAQs.length > 0 && (
        <div className="text-center text-sm text-[#F7F5F2]/60">
          Menampilkan {filteredFAQs.length} dari {faqs.length} FAQ
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminFAQ;
